import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getCurrentUser, User } from '../services/authService';
import { adminService, AdminStats } from '../services/adminService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import { APP_NAME } from '../constants';

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
`;

const DashboardCard = styled.div<{ color?: string }>`
  background: linear-gradient(135deg, ${props => props.color || '#381f74'} 0%, ${props => props.color ? `${props.color}dd` : '#2a1654'} 100%);
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(30px, -30px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0;
  flex: 1;
`;

const CardStat = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const CardAction = styled.div`
  margin-top: 20px;
  position: relative;
  z-index: 1;
`;

const LoadingPlaceholder = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const AccessDenied = styled.div`
  text-align: center;
  padding: 40px;
  background-color: #fff3e0;
  border-radius: 16px;
  margin-top: 20px;
`;

const AccessDeniedTitle = styled.h2`
  font-size: 24px;
  color: #e65100;
  margin-bottom: 16px;
`;

const AccessDeniedText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
`;

const SuccessMessage = styled.div`
  background-color: #4caf50;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const CleanupSection = styled.div`
  margin-top: 30px;
  padding: 20px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const CleanupTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
`;

const CleanupDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const AdminDashboard: React.FC = () => {  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        if (userData) {
          setUser(userData);
          
          // Check if user is admin
          if (adminService.isAdmin(userData)) {
            const adminStats = await adminService.getAdminStats();
            setStats(adminStats);
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
        setError('Failed to load admin dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleViewAllUsers = () => {
    navigate('/admin/users');
  };

  const handleViewAllBusinesses = () => {
    navigate('/admin/businesses');
  };

  const handleViewPendingBusinesses = () => {
    navigate('/admin/businesses?filter=pending');
  };
  const handleViewDeletionRequests = () => {
    navigate('/admin/users?marked-for-deletion=true');
  };
  const handleCleanupUnverifiedBusinesses = async () => {
    setCleanupLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const functions = getFunctions();
      const manualCleanupFunction = httpsCallable(functions, 'manualCleanupUnverifiedBusinesses');
      
      const result = await manualCleanupFunction();
      const data = result.data as any;
      
      if (data.success) {
        setSuccessMessage(data.message);
        
        // Refresh admin stats to show updated numbers
        if (user && adminService.isAdmin(user)) {
          const adminStats = await adminService.getAdminStats();
          setStats(adminStats);
        }
      } else {
        setError('Cleanup failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error during cleanup:', error);
      setError('Failed to cleanup unverified businesses: ' + (error.message || 'Unknown error'));
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleDebugBusinesses = async () => {
    setDebugLoading(true);
    setError('');
    setDebugInfo(null);

    try {
      const functions = getFunctions();
      const debugFunction = httpsCallable(functions, 'debugBusinesses');
      
      const result = await debugFunction();
      const data = result.data as any;
      
      if (data.success) {
        setDebugInfo(data);
        console.log('Debug info:', data);
      } else {
        setError('Debug failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error during debug:', error);
      setError('Failed to debug businesses: ' + (error.message || 'Unknown error'));
    } finally {
      setDebugLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingPlaceholder>Loading admin dashboard...</LoadingPlaceholder>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  if (!adminService.isAdmin(user)) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Access Denied</title>
        </Helmet>
        
        <Container>
          <ContentWrapper>
            <TopBar user={user} />
            
            <AccessDenied>
              <AccessDeniedTitle>Access Denied</AccessDeniedTitle>
              <AccessDeniedText>
                You don't have permission to access the admin dashboard.
              </AccessDeniedText>
              <Button
                text="Back to Dashboard"
                onClick={handleNavigateToMyAccount}
                bgColor="#381f74"
                textColor="white"
                width="auto"
                minWidth="160px"
              />
            </AccessDenied>
          </ContentWrapper>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Admin Dashboard</title>
        <meta name="description" content="Admin dashboard for managing users and businesses" />
      </Helmet>
      
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          
          <PageTitle>Admin Dashboard</PageTitle>
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Admin Dashboard</span>
          </Breadcrumbs>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
          
          {stats && (
            <DashboardGrid>
              <DashboardCard color="#1976d2" onClick={handleViewAllUsers}>
                <CardHeader>
                  <CardTitle>Users</CardTitle>
                  <CardStat>{stats.totalUsers}</CardStat>
                </CardHeader>
                <CardAction>
                  <Button
                    text="Manage Users"
                    onClick={handleViewAllUsers}
                    bgColor="rgba(255, 255, 255, 0.2)"
                    textColor="white"
                    width="100%"
                  />
                </CardAction>
              </DashboardCard>

              <DashboardCard color="#388e3c" onClick={handleViewAllBusinesses}>
                <CardHeader>
                  <CardTitle>Businesses</CardTitle>
                  <CardStat>{stats.totalBusinesses}</CardStat>
                </CardHeader>
                <CardAction>
                  <Button
                    text="Manage Businesses"
                    onClick={handleViewAllBusinesses}
                    bgColor="rgba(255, 255, 255, 0.2)"
                    textColor="white"
                    width="100%"
                  />
                </CardAction>
              </DashboardCard>

              <DashboardCard color="#ff9800" onClick={handleViewPendingBusinesses}>
                <CardHeader>
                  <CardTitle>Pending</CardTitle>
                  <CardStat>{stats.pendingBusinesses}</CardStat>
                </CardHeader>
                <CardAction>
                  <Button
                    text="Review Pending"
                    onClick={handleViewPendingBusinesses}
                    bgColor="rgba(255, 255, 255, 0.2)"
                    textColor="white"
                    width="100%"
                  />
                </CardAction>
              </DashboardCard>

              <DashboardCard color="#f44336" onClick={handleViewDeletionRequests}>
                <CardHeader>
                  <CardTitle>Deletions</CardTitle>
                  <CardStat>{stats.usersMarkedForDeletion}</CardStat>
                </CardHeader>
                <CardAction>
                  <Button
                    text="View Requests"
                    onClick={handleViewDeletionRequests}
                    bgColor="rgba(255, 255, 255, 0.2)"
                    textColor="white"
                    width="100%"
                  />
                </CardAction>              </DashboardCard>
            </DashboardGrid>
          )}          {/* Business Cleanup Section */}
          <CleanupSection>
            <CleanupTitle>Business Cleanup</CleanupTitle>
            <CleanupDescription>
              Remove businesses that have been pending verification for more than 5 days. The system automatically runs this cleanup daily at 2 AM UTC.
            </CleanupDescription>
            
            {error && (
              <div style={{ color: 'red', marginBottom: '10px', fontSize: '14px' }}>
                {error}
              </div>
            )}
            
            {successMessage && (
              <div style={{ color: 'green', marginBottom: '10px', fontSize: '14px' }}>
                {successMessage}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <Button
                text={cleanupLoading ? "Cleaning up..." : "Cleanup"}
                onClick={handleCleanupUnverifiedBusinesses}
                loading={cleanupLoading}
                bgColor="#ff5722"
                textColor="white"
                width="auto"
                minWidth="200px"
                disabled={cleanupLoading || debugLoading}
              />
              
              <Button
                text={debugLoading ? "Debugging..." : "Logs"}
                onClick={handleDebugBusinesses}
                loading={debugLoading}
                bgColor="#2196f3"
                textColor="white"
                width="auto"
                minWidth="150px"
                disabled={cleanupLoading || debugLoading}
              />
            </div>
            
            {debugInfo && (
              <div style={{ 
                background: '#f5f5f5', 
                padding: '15px', 
                borderRadius: '8px', 
                fontSize: '14px',
                fontFamily: 'monospace'
              }}>
                <h4>Debug Information:</h4>
                <p><strong>Total Businesses:</strong> {debugInfo.totalBusinesses}</p>
                <p><strong>Pending Businesses:</strong> {debugInfo.pendingBusinesses}</p>
                <p><strong>Threshold Date:</strong> {new Date(debugInfo.fiveDaysAgoThreshold).toLocaleString()}</p>
                
                {debugInfo.businesses && debugInfo.businesses.length > 0 && (
                  <div>
                    <h5>Pending Businesses:</h5>
                    {debugInfo.businesses.map((business: any, index: number) => (
                      <div key={index} style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                        <p><strong>Name:</strong> {business.name}</p>
                        <p><strong>Status:</strong> {business.status}</p>
                        <p><strong>Created:</strong> {business.createdAt ? new Date(business.createdAt).toLocaleString() : 'N/A'}</p>
                        <p><strong>Should Delete:</strong> {business.isOld ? 'Yes' : 'No'}</p>
                        <hr style={{ margin: '5px 0' }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CleanupSection>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default AdminDashboard;
